import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { CirclePicker } from 'react-color';
import { colors } from 'shared/defaultTheme';
import { LabelInput, LabelRequiredInput } from './Labels';
import { Button } from './Buttons';

const defaultColors = [
  '#AF9F01',
  '#B87300',
  '#BC5901',
  '#C10F0B',
  '#830065',
  '#4A006F',
  '#001478',
  '#01417F',
  '#009598',
  '#00773B',
  '#008B07',
  '#341909',
  '#000000',
  '#FFF201',
  '#FFB001',
  '#EB6F00',
  '#FF3734',
  '#D011A2',
  '#9470C0',
  '#003FAE',
  '#3B92D6',
  '#00D2D6',
  '#00B165',
  '#00CF2A',
  '#661A01',
  '#898989',
];

const PickerContainer = styled.div(({ hasBorder }) => ({
  border: hasBorder ? `1px solid ${colors.lightSilver}` : 'none',
}));

const CircleColorSelected = styled.div(({ backgroundColor }) => ({
  backgroundColor: backgroundColor || 'transparent',
  border: backgroundColor ? 'none !important' : '1px solid',
}));

export const ColorPicker = ({
  label,
  className,
  required,
  labelColor,
  labelClass,
  withBorder,
  selectedColor,
  onChange,
}) => {
  const { t } = useTranslation();
  const [selectedOtherColor, setSelectedOtherColor] = useState('#');

  const onChangePicker = (color) => {
    setSelectedOtherColor('#');
    onChange(color.hex);
  };

  const onChangeOtherColor = (e) => {
    if (e.target.value.includes('#')) {
      setSelectedOtherColor(e.target.value);
      return;
    }
    setSelectedOtherColor(`#${e.target.value}`);
  };

  const onClickSelectedOtherColor = () => {
    onChange(selectedOtherColor);
  };

  return (
    <div className={classNames(['color-picker', className])}>
      {required ? (
        <LabelRequiredInput color={labelColor} label={label} className={labelClass} />
      ) : (
        <LabelInput color={labelColor} label={label} className={labelClass} />
      )}
      <PickerContainer className="color-picker__picker-container" hasBorder={withBorder}>
        <div className="color-picker__picker-container__column-picker">
          <CirclePicker
            color={selectedColor}
            width="unset"
            colors={defaultColors}
            circleSize={32}
            circleSpacing={22}
            onChangeComplete={onChangePicker}
          />
        </div>
        <div className="color-picker__picker-container__column-color-selected">
          <CircleColorSelected className="circle-color-selected" backgroundColor={selectedColor}>
            {selectedColor && (
              <span className="body-text--secondary text-uppercase">{selectedColor}</span>
            )}
          </CircleColorSelected>
          <div className="selected-other-color-container">
            <input
              className="selected-other-color-input"
              value={selectedOtherColor}
              onChange={onChangeOtherColor}
            />
            <Button
              className="selected-other-color-button"
              label={t('general_select_other')}
              color="primary"
              type="button"
              backgroundColor={colors.ecru}
              textColor={colors.white}
              onClick={onClickSelectedOtherColor}
            />
          </div>
        </div>
      </PickerContainer>
    </div>
  );
};

ColorPicker.defaultProps = {
  className: '',
  required: false,
  labelColor: colors.blueLefebvre,
  labelClass: '',
  withBorder: true,
  selectedColor: undefined,
};

ColorPicker.propTypes = {
  label: PropTypes.string.isRequired,
  className: PropTypes.string,
  required: PropTypes.bool,
  labelColor: PropTypes.string,
  labelClass: PropTypes.string,
  withBorder: PropTypes.bool,
  selectedColor: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};
